import { useUser, useAuth } from "@clerk/clerk-react";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useRef, useState } from "react";

/**
 * Phase A audit fix A1 — Clerk → Supabase Auth bridge.
 *
 * Sequence on first sign-in for any Clerk user:
 *   1. Call /functions/v1/clerk-link with Clerk JWT — sets Clerk external_id
 *      to a deterministic uuid v5, creates a matching auth.users row.
 *   2. Call getToken({ template: 'supabase' }) — emits JWT with claim
 *      user_metadata.supabase_uuid = the uuid (set in step 1).
 *   3. setSession(clerkJWT) — Supabase trusts the JWT via the configured
 *      Third-Party Auth provider (Clerk JWKS).
 *   4. Upsert profile + subscription. RLS via current_user_uuid() picks up
 *      the uuid from the JWT's user_metadata.supabase_uuid claim.
 *
 * Idempotent: clerk-link is a no-op if external_id is already set, and the
 * upserts use onConflict to handle re-runs.
 *
 * Returns { ready: boolean } so consumers can wait on the bridge.
 */
export function useClerkSupabaseSync(): { ready: boolean; supabaseUuid: string | null } {
  const { user, isLoaded } = useUser();
  const { getToken } = useAuth();
  const syncedRef = useRef<string | null>(null);
  const [ready, setReady] = useState(false);
  const [supabaseUuid, setSupabaseUuid] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoaded || !user) return;
    if (syncedRef.current === user.id) {
      setReady(true);
      return;
    }

    async function sync() {
      try {
        // 1. Get the "supabase"-template Clerk JWT. The default-template
        // token doesn't carry the audience Supabase's TPA validator
        // expects, and the platform returns UNAUTHORIZED_ASYMMETRIC_JWT
        // before our edge fn ever runs. The "supabase" template emits the
        // correct aud + user_metadata.supabase_uuid for Supabase TPA.
        const bootstrapToken = await getToken({ template: "supabase" });
        if (!bootstrapToken) {
          console.error("[ClerkSync] No Clerk supabase-template token — aborting sync");
          return;
        }

        // 2. Call clerk-link — sets external_id + creates auth.users row if missing
        const linkRes = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clerk-link`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${bootstrapToken}`,
              apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              "Content-Type": "application/json",
            },
          }
        );
        if (!linkRes.ok) {
          const body = await linkRes.text();
          console.error("[ClerkSync] clerk-link failed:", linkRes.status, body.slice(0, 300));
          return;
        }
        const { supabase_uuid: sbUuid } = await linkRes.json();
        if (!sbUuid) {
          console.error("[ClerkSync] clerk-link returned no uuid");
          return;
        }
        setSupabaseUuid(sbUuid);

        // Reload Clerk user so user.externalId reflects the bridge's PATCH.
        // The supabase client's accessToken callback (src/integrations/supabase/client.ts)
        // will pick up the fresh JWT automatically on every call — no setSession needed.
        await user!.reload();

        // 5. Upsert profile by user_id (= auth.users.id = sbUuid). After the
        // 20260515_profile_id_eq_user_id migration, profile.id == user_id, so
        // the handle_new_user() trigger and this upsert converge to the same row.
        // We never set id explicitly — let the trigger / default own it.
        const { error: profileError } = await supabase.from("profiles").upsert(
          {
            user_id: sbUuid,
            email: user!.primaryEmailAddress?.emailAddress ?? null,
            role: "parent",
            updated_at: new Date().toISOString(),
          } as any,
          { onConflict: "user_id" }
        );
        if (profileError) {
          console.error("[ClerkSync] Profile upsert failed:", profileError.message);
          return;
        }

        // Subscription bootstrap was previously auto-inserting a 'trial' row
        // here so users could enter the app without a payment method on file.
        // The new flow (per 2026-05-16 brief) is: /sign-up → /subscribe →
        // Stripe Checkout → stripe-webhook creates the row → /onboarding.
        // No auto-bootstrap — users without a Stripe-backed subscription get
        // redirected to /subscribe by ProtectedRoute.

        // Hydrate localStorage 'screentime-profiles' from the children table.
        // Ollie's ParentPortal + Playground read children from localStorage
        // (legacy device-id-keyed flow). Without this, signing in on a fresh
        // browser/incognito leaves /parent empty even though the user has
        // children in the DB.
        const { data: kids } = await supabase
          .from("children")
          .select("id, name, miner_type, date_of_birth, pin")
          .eq("parent_id", sbUuid)
          .order("created_at", { ascending: true });
        if (kids && kids.length > 0) {
          const localProfiles = kids.map((k: any) => ({
            id: k.id,
            name: k.name,
            avatar: "avatar-0",
            ageGroup: k.miner_type === "junior" ? "junior" : "pro",
            pin: k.pin ?? undefined,
            dateOfBirth: k.date_of_birth ?? undefined,
          }));
          const existingRaw = localStorage.getItem("screentime-profiles");
          let existing: any[] = [];
          try { existing = existingRaw ? JSON.parse(existingRaw) : []; } catch { existing = []; }
          // Merge: prefer DB-version of any id present in both; preserve any
          // local-only profiles (e.g. unsynced new-child onboarding).
          const dbIds = new Set(localProfiles.map((p) => p.id));
          const merged = [...localProfiles, ...existing.filter((e: any) => !dbIds.has(e.id))];
          localStorage.setItem("screentime-profiles", JSON.stringify(merged));
          // Notify same-tab consumers (storage events only fire cross-tab natively).
          window.dispatchEvent(new Event("screentime-profiles-updated"));
        }

        syncedRef.current = user!.id;
        setReady(true);
      } catch (err) {
        console.error("[ClerkSync] Unexpected error:", err);
      }
    }

    sync();

    // No manual refresh loop needed — supabase client's accessToken callback
    // pulls a fresh JWT per request, so Clerk's auto-rotated session token
    // is always used.
  }, [user, isLoaded, getToken]);

  return { ready, supabaseUuid };
}
